import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { usePreferencesStore } from '@/stores/preferences';
import { useUsageStore } from '@/stores/usage';
import { billingService } from '@/services/billing';
import { Card, Button } from '@/components/UI';
import { UsageIndicator } from '@/components/UI';
import type { Plan, BillingInfo } from '@/types';

type SettingsTab = 'subscription' | 'organization' | 'account';

export function OrgSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization } = useAuthStore();
  const { userRole, fetchCurrentOrganization } = useOrganizationStore();

  // Fetch organization to get user role
  useEffect(() => {
    if (currentOrganization?.id) {
      fetchCurrentOrganization(currentOrganization.id);
    }
  }, [currentOrganization?.id]);

  const isOwner = userRole === 'owner';
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabParam && ['subscription', 'organization', 'account'].includes(tabParam)
      ? tabParam
      : 'account'
  );

  // Sync tab with URL params
  useEffect(() => {
    if (tabParam && ['subscription', 'organization', 'account'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (isOwner && !tabParam) {
      setActiveTab('organization');
    }
  }, [tabParam, isOwner]);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
          Dashboard
        </Link>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-gray-900 font-medium">Settings</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your subscription and account</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {isOwner && (
            <button
              onClick={() => handleTabChange('organization')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'organization'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Organization
              {currentOrganization?.plan === 'pro' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Pro
                </span>
              )}
            </button>
          )}
          {isOwner && (
            <button
              onClick={() => handleTabChange('subscription')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscription'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Subscription
            </button>
          )}
          <button
            onClick={() => handleTabChange('account')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'account'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Account
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {isOwner && activeTab === 'subscription' && <SubscriptionTab />}
      {isOwner && activeTab === 'organization' && <OrganizationTab />}
      {activeTab === 'account' && <AccountTab />}
    </div>
  );
}

function SubscriptionTab() {
  const { currentOrganization, refreshOrganization } = useAuthStore();
  const { usage, fetchUsage } = useUsageStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const currentPlan = currentOrganization?.plan || 'free';

  // Check for success redirect from Stripe
  useEffect(() => {
    if (searchParams.get('success') === '1') {
      setShowSuccess(true);
      // Remove success param from URL
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
      // Refresh organization data to get updated plan
      refreshOrganization();
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!currentOrganization) return;

      try {
        const [plansData, billing] = await Promise.all([
          billingService.getPlans(),
          billingService.getSubscription(currentOrganization.id),
          fetchUsage(currentOrganization.id),
        ]);
        setPlans(plansData);
        setBillingInfo(billing);
      } catch (error) {
        console.error('Failed to load billing data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentOrganization]);

  const handleManageBilling = async () => {
    if (!currentOrganization) return;

    setIsLoadingPortal(true);
    try {
      const portalUrl = await billingService.createPortalSession(currentOrganization.id);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleUpgrade = async (planId: string) => {
    if (!currentOrganization || planId === currentPlan) return;

    setIsUpgrading(planId);
    try {
      const checkoutUrl = await billingService.createCheckoutSession(
        currentOrganization.id,
        planId
      );
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setIsUpgrading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Your subscription has been updated successfully!
        </div>
      )}

      {/* Current Plan Summary */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Current Plan</h3>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                {currentPlan}
              </span>
              <span className="text-gray-600 text-sm">
                {currentOrganization?.name}
              </span>
            </div>
            {billingInfo?.subscription && (
              <p className="text-sm text-gray-500 mt-1">
                {billingInfo.subscription.cancel_at_period_end
                  ? 'Cancels at end of period'
                  : `Renews on ${new Date(billingInfo.subscription.current_period_end * 1000).toLocaleDateString()}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="w-48">
              <UsageIndicator
                current={usage?.usage || 0}
                limit={usage?.limit || currentOrganization?.request_limit || 5000}
                label="Requests"
              />
            </div>
            {currentPlan !== 'free' && (
              <Button
                variant="secondary"
                onClick={handleManageBilling}
                disabled={isLoadingPortal}
              >
                {isLoadingPortal ? 'Loading...' : 'Manage Billing'}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Plan Comparison */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan === plan.id;
          const isPopular = plan.id === 'growth';

          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-xl border-2 p-6 flex flex-col ${
                isCurrentPlan ? 'border-primary-500' : 'border-gray-200'
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  {plan.price > 0 && <span className="text-gray-600">/month</span>}
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  {plan.requests === -1 ? 'Unlimited' : plan.requests.toLocaleString()} requests/mo
                </p>
              </div>

              <ul className="space-y-3 mb-6 flex-1">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
                {plan.excluded_features?.map((feature, index) => (
                  <li key={`ex-${index}`} className="flex items-center text-sm text-gray-400">
                    <svg className="w-5 h-5 text-gray-300 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                {isCurrentPlan && currentPlan !== 'free' ? (
                  <Button className="w-full" onClick={handleManageBilling} disabled={isLoadingPortal}>
                    {isLoadingPortal ? 'Loading...' : 'Manage Subscription'}
                  </Button>
                ) : currentPlan === 'free' && plan.id !== 'free' ? (
                  <Button
                    className="w-full"
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isUpgrading !== null}
                  >
                    {isUpgrading === plan.id ? 'Loading...' : 'Upgrade'}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-gray-500">
        Need more? <a href="mailto:hello@statsmatic.io" className="text-primary-600 hover:text-primary-700">Contact us</a> for custom enterprise plans.
      </p>

      {/* FAQ */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900">What counts as a request?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Each pageview tracked by our script counts as one request. API calls don't count against your limit.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">What happens if I exceed my limit?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Your tracking will continue, but you'll receive a notification to upgrade. We never stop tracking mid-month.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Can I downgrade at any time?</h4>
            <p className="text-sm text-gray-600 mt-1">
              Yes, you can downgrade at any time. Your plan will change at the end of your billing period.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function OrganizationTab() {
  const { currentOrganization, refreshOrganization } = useAuthStore();
  const {
    userRole,
    updateOrganization,
    members,
    fetchMembers,
    removeMember,
  } = useOrganizationStore();

  const [orgName, setOrgName] = useState(currentOrganization?.name || '');
  const [isSavingName, setIsSavingName] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const isOwner = userRole === 'owner';
  const hasTeamFeature = currentOrganization?.plan === 'pro';

  useEffect(() => {
    if (currentOrganization) {
      setOrgName(currentOrganization.name);
      if (hasTeamFeature) {
        fetchMembers(currentOrganization.id);
      }
    }
  }, [currentOrganization?.id, hasTeamFeature]);

  const handleSaveOrgName = async () => {
    if (!currentOrganization || !orgName.trim() || orgName === currentOrganization.name) return;

    setIsSavingName(true);
    try {
      await updateOrganization(currentOrganization.id, { name: orgName.trim() });
      await refreshOrganization?.();
    } finally {
      setIsSavingName(false);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization || !inviteEmail.trim()) return;

    setIsSendingInvite(true);
    try {
      // TODO: Implement invitation API
      console.log('Sending invite to:', inviteEmail);
      setInviteEmail('');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!currentOrganization) return;
    if (!confirm('Remove this member from the organization?')) return;

    await removeMember(currentOrganization.id, userId);
  };

  return (
    <div className="space-y-6">
      {/* Organization Settings */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization</h3>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={!isOwner}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${isOwner ? '' : 'bg-gray-50'} text-gray-700`}
            />
          </div>
          {isOwner && orgName !== currentOrganization?.name && (
            <div className="flex items-end">
              <Button onClick={handleSaveOrgName} disabled={isSavingName}>
                {isSavingName ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Invite Team Member */}
      {isOwner && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h3>
          {hasTeamFeature ? (
            <form onSubmit={handleSendInvite} className="flex gap-4">
              <div className="flex-1">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <Button type="submit" disabled={isSendingInvite || !inviteEmail.trim()}>
                {isSendingInvite ? 'Sending...' : 'Send Invite'}
              </Button>
            </form>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-600 text-sm mb-3">
                Invite team members to collaborate on your analytics. Available on the Pro and Enterprise plans.
              </p>
              <Link to="/settings?tab=subscription">
                <Button variant="secondary">
                  Upgrade to Pro
                </Button>
              </Link>
            </div>
          )}
        </Card>
      )}

      {/* Team Members */}
      {isOwner && hasTeamFeature && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Team Members ({members.length})
          </h3>
          <div className="divide-y divide-gray-100">
            {members.length === 0 ? (
              <p className="py-4 text-sm text-gray-500">No team members yet.</p>
            ) : (
              members.map((member) => (
                <div key={member.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {member.user_id?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">User {member.user_id.slice(0, 8)}...</p>
                      <p className="text-sm text-gray-500 capitalize">{member.role}</p>
                    </div>
                  </div>
                  {member.role !== 'owner' && (
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
];

function formatTzLabel(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
    const parts = formatter.formatToParts(now);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return `${tz.replace(/_/g, ' ')} (${offset})`;
  } catch {
    return tz;
  }
}

function AccountTab() {
  const { user, deleteAccount } = useAuthStore();
  const { timezone, updateTimezone, fetchPreferences } = usePreferencesStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [tzSaved, setTzSaved] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleTimezoneChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTz = e.target.value;
    setSavingTimezone(true);
    setTzSaved(false);
    await updateTimezone(newTz);
    setSavingTimezone(false);
    setTzSaved(true);
    setTimeout(() => setTzSaved(false), 2000);
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;

    setIsDeleting(true);
    try {
      await deleteAccount();
      window.location.href = '/';
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Information */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
            <input
              type="text"
              value={user?.id || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm"
            />
          </div>
        </div>
      </Card>

      {/* Timezone */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timezone</h3>
        <p className="text-sm text-gray-600 mb-3">
          Used for date range calculations in your analytics dashboard (e.g. "Today", "Yesterday").
        </p>
        <div className="flex items-center gap-3">
          <select
            value={timezone}
            onChange={handleTimezoneChange}
            disabled={savingTimezone}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {!COMMON_TIMEZONES.includes(timezone) && (
              <option value={timezone}>{formatTzLabel(timezone)}</option>
            )}
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{formatTzLabel(tz)}</option>
            ))}
          </select>
          {savingTimezone && (
            <span className="text-sm text-gray-500">Saving...</span>
          )}
          {tzSaved && (
            <span className="text-sm text-green-600">Saved</span>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <h3 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h3>
        <p className="text-gray-600 text-sm mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <Button
          variant="secondary"
          className="text-red-600 border-red-300 hover:bg-red-50"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Account
        </Button>
      </Card>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Delete Account</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete your account? This action cannot be undone.
            </p>
            <ul className="text-sm text-gray-600 mb-4 space-y-1">
              <li>• Your account will be permanently deleted</li>
              <li>• All organizations you own will be deleted</li>
              <li>• All sites and analytics data will be deleted</li>
            </ul>
            <p className="text-sm text-gray-700 mb-4">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleDeleteAccount}
                disabled={confirmText !== 'DELETE' || isDeleting}
                isLoading={isDeleting}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrgSettings;
